import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BkPage, Card } from "@/components/bookkeeping/BkPage";

const Pick = ({ value, options, onChange, testId }) => (
  <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value || null)}
    className="w-full max-w-sm rounded-lg border border-[#333] bg-[#0F0F0F] px-3 py-2 text-sm text-white outline-none focus:border-[#4CCC06]">
    <option value="">Not mapped</option>
    {options.map((o) => <option key={o.qbo_id} value={o.qbo_id}>{o.name}{o.account_type ? ` (${o.account_type})` : ""}</option>)}
  </select>
);

const Body = ({ entity }) => {
  const [m, setM] = useState(null);
  const load = useCallback(() => api(`/bookkeeping/qbo/${entity.id}/mapping`).then(setM).catch((e) => toast.error(e.message)), [entity.id]);
  useEffect(() => { setM(null); load(); }, [load]);
  const save = async (path, body) => { try { await api(path, { method: "PUT", body }); toast.success("Saved"); load(); } catch (e) { toast.error(e.message); } };
  if (!m) return null;
  if (!m.accounts.length) return <p className="rounded-xl border border-dashed border-[#333] p-4 text-sm text-neutral-400" data-testid="bk-qbo-mapping-empty">Connect this entity's QuickBooks file on the Connections tab first. The chart of accounts appears here once it's pulled.</p>;
  return (
    <>
      <Card title="PORTAL CONCEPTS → QUICKBOOKS" testId="bk-qbo-concepts">
        <div className="divide-y divide-[#262626]">
          {m.concepts.map((c) => (
            <div key={c.key} className="flex flex-wrap items-center gap-3 py-2" data-testid={`bk-concept-${c.key}`}>
              <span className="min-w-0 flex-1 text-sm font-bold text-white">{c.label}</span>
              <Pick testId={`bk-concept-select-${c.key}`} value={c.qbo_id} options={c.kind === "item" ? m.items : m.accounts} onChange={(v) => save(`/bookkeeping/qbo/${entity.id}/mapping/${c.key}`, { qbo_id: v })} />
            </div>
          ))}
        </div>
      </Card>
      <Card title="BANK ACCOUNTS → QUICKBOOKS" testId="bk-qbo-banks">
        {m.bank_accounts.length === 0 ? <p className="text-sm text-neutral-500">No linked bank accounts yet.</p> : m.bank_accounts.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center gap-3 py-2" data-testid={`bk-bankmap-${b.id}`}>
            <span className="min-w-0 flex-1 text-sm font-bold text-white">{b.name} ••{b.mask}</span>
            <Pick testId={`bk-bankmap-select-${b.id}`} value={b.qbo_account_id} options={m.accounts.filter((a) => ["Bank", "Credit Card"].includes(a.account_type))} onChange={(v) => save(`/bookkeeping/bank-accounts/${b.id}/qbo`, { qbo_account_id: v })} />
          </div>
        ))}
      </Card>
      <p className="text-xs text-neutral-500">Default expense accounts per vendor are set with rules in Phase 3.</p>
    </>
  );
};

export default function QboMapping() {
  return <BkPage title="QuickBooks mapping" testId="bookkeeping-qbo-mapping">{({ entity }) => <Body entity={entity} />}</BkPage>;
}
